import React from "react";
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import Header from "../../components/Header";
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, get, set, update, onValue } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useParams, useNavigate } from "react-router-dom";
import CodeMirror from "react-codemirror";
import "./Coding.css";
import "./dracula.css";
import "./CompanyInterviewEditor.css";
import Dropdown from "../../components/Dropdown";
import { IconContext } from "react-icons";
import { FiSave, FiEye, FiUsers, FiSettings, FiRefreshCw, FiChevronDown, FiChevronUp, FiX, FiExternalLink, FiUpload, FiTrash2, FiSearch, FiLink, FiUser } from "react-icons/fi";
import DashboardSidebar from "../../components/DashboardSidebar";
import InterviewEditorChatPanel from "../../components/InterviewEditorChatPanel";
import { CompanyInterviewEditorProps, CompanyInterviewEditorState, InterviewSettings, CandidateDocument, User } from "../../types";
import { generateInterviewPrompt, getBasePromptTemplate, getPromptPreview, validateCustomPrompt } from "../../lib/interviewer-prompt";

require("codemirror/lib/codemirror.css");
require("codemirror/mode/javascript/javascript");
require("codemirror/mode/python/python");
require("codemirror/mode/ruby/ruby");
require("codemirror/mode/swift/swift");
require("codemirror/mode/go/go");
require("codemirror/mode/php/php");
require("codemirror/mode/r/r");
require("codemirror/mode/clike/clike"); // For Java-like languages

// Create a wrapper component to use hooks
function CompanyInterviewEditorWrapper(props: { darkMode: boolean }) {
    const params = useParams<{ interviewId: string }>();
    const navigate = useNavigate();
    return <CompanyInterviewEditor {...props} interviewId={params.interviewId || ''} navigate={navigate} />;
}

class CompanyInterviewEditor extends React.Component<CompanyInterviewEditorProps, CompanyInterviewEditorState> {
    private auth = getAuth();
    private db = getDatabase();
    private storage = getStorage();
    private fileInputRef = React.createRef<HTMLInputElement>();
    private codeMirrorRef: any = null;
    private saveTimeout: any = null;
    private codeFirebaseTimeout: ReturnType<typeof setTimeout> | null = null;
    private isTypingCode = false;

    constructor(props: CompanyInterviewEditorProps) {
        super(props);
        this.state = {
            user: null,
            login: false,
            loading: true,
            interview: {
                title: '',
                description: '',
                content: '',
                language: 'Python',
                difficulty: 'Medium',
                status: 'draft',
                instructions: '',
                timeLimit: 60,
                allowedLanguages: ['Python', 'JavaScript', 'Java'],
                createdAt: '',
                updatedAt: '',
                agentSystemPrompt: undefined,
                interviewType: 'coding',
                systemDesignProblem: undefined,
                systemDesignPrompt: undefined,
                candidateName: '',
                candidateLinkedIn: '',
                candidateDocuments: [],
                candidateResearchData: '',
                candidateResearchStatus: 'idle'
            },
            isEditing: false,
            hasChanges: false,
            showAgentConfig: false,
            showCandidateProfile: false,
            promptPreview: '',
            showPreviewModal: false,
            uploadingDocument: false,
            researchLoading: false,
            researchError: null,
            publishSuccess: null
        };

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleCodeChange = this.handleCodeChange.bind(this);
        this.saveInterview = this.saveInterview.bind(this);
        this.publishInterview = this.publishInterview.bind(this);
        this.loadInterview = this.loadInterview.bind(this);
        this.toggleAgentConfig = this.toggleAgentConfig.bind(this);
        this.loadDefaultPrompt = this.loadDefaultPrompt.bind(this);
        this.updatePromptPreview = this.updatePromptPreview.bind(this);
        this.openPreviewModal = this.openPreviewModal.bind(this);
        this.closePreviewModal = this.closePreviewModal.bind(this);
        this.toggleCandidateProfile = this.toggleCandidateProfile.bind(this);
        this.handleDocumentUpload = this.handleDocumentUpload.bind(this);
        this.removeDocument = this.removeDocument.bind(this);
        this.handleDocumentUpload = this.handleDocumentUpload.bind(this);
        this.removeDocument = this.removeDocument.bind(this);
        this.runCandidateResearch = this.runCandidateResearch.bind(this);
        this.handleLogout = this.handleLogout.bind(this);
        this.handleSettingsUpdated = this.handleSettingsUpdated.bind(this);
    }

    handleSettingsUpdated(updates: Partial<InterviewSettings>): void {
        this.setState({
            interview: {
                ...this.state.interview,
                ...updates
            },
            hasChanges: true
        }, () => {
            if (updates.content !== undefined && this.codeMirrorRef?.getCodeMirror?.()) {
                this.codeMirrorRef.getCodeMirror().setValue(updates.content);
            }
            if (['title', 'description', 'content', 'instructions', 'difficulty', 'timeLimit', 'allowedLanguages', 'agentSystemPrompt', 'interviewType', 'systemDesignProblem', 'systemDesignPrompt'].some(f => f in updates)) {
                this.updatePromptPreview();
            }
        });
    }

    componentDidMount(): void {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        window.addEventListener('pagehide', this.handlePageHide);
        onAuthStateChanged(this.auth, (user) => {
            if (user) {
                this.setState({
                    user: user as User,
                    login: true,
                    loading: false
                }, () => {
                    this.loadInterview();
                });
            } else {
                this.setState({
                    login: false,
                    loading: false
                });
                this.props.navigate("/login");
            }
        });
    }

    handlePageHide = (e: PageTransitionEvent): void => {
        if (this.state.hasChanges && this.state.user) {
            // Synchronously save via Firebase before the page dies
            const userId = this.state.user.uid;
            const interviewId = this.props.interviewId;
            const content = this.codeMirrorRef?.getCodeMirror?.()?.getValue?.() ?? this.state.interview.content;
            const updatedInterview = {
                ...this.state.interview,
                content,
                updatedAt: new Date().toISOString()
            };
            const cleanedInterview = this.removeUndefinedValues(updatedInterview);
            update(ref(this.db, `company-interviews/${userId}/${interviewId}`), cleanedInterview);
        }
    }

    handleBeforeUnload = (e: BeforeUnloadEvent): void => {
        if (this.state.hasChanges) {
            // Attempt to save immediately
            this.saveInterview(true);

            // Show standard browser confirmation dialog
            e.preventDefault();
            e.returnValue = '';
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        window.removeEventListener('pagehide', this.handlePageHide);
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        if (this.codeFirebaseTimeout) {
            clearTimeout(this.codeFirebaseTimeout);
        }
        if (this.state.hasChanges) {
            this.saveInterview(true);
        }
    }

    async loadInterview(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const interviewId = this.props.interviewId;

        try {
            const interviewRef = ref(this.db, `company-interviews/${userId}/${interviewId}`);
            const snapshot = await get(interviewRef);

            if (snapshot.exists()) {
                const interviewData = snapshot.val();
                const updatedInterview = {
                    ...this.state.interview,
                    ...interviewData,
                    content: interviewData.content ?? this.state.interview.content ?? ''
                };
                this.setState({
                    interview: updatedInterview
                }, () => {
                    this.updatePromptPreview();
                    // Explicitly set CodeMirror value - react-codemirror can miss prop updates from async load
                    setTimeout(() => {
                        if (this.codeMirrorRef?.getCodeMirror?.()) {
                            const content = updatedInterview.content ?? '';
                            this.codeMirrorRef.getCodeMirror().setValue(content);
                        }
                    }, 0);
                });
            } else {
                // Create new interview if it doesn't exist
                const newInterview: InterviewSettings = {
                    title: 'New Interview',
                    description: 'A new coding interview session',
                    content: '# Welcome to your Interview Sandbox!\n\n# This interview session is designed to evaluate coding skills.\n# Candidates can write code, run it, and collaborate in real-time.\n\n# Instructions:\n# 1. Solve the problem below\n# 2. Explain your approach\n# 3. Optimize if possible\n\nprint("Hello, Interview!")',
                    language: 'Python',
                    difficulty: 'Medium',
                    status: 'draft',
                    instructions: 'Please solve the coding problem below. You have 60 minutes to complete the task.',
                    timeLimit: 60,
                    allowedLanguages: ['Python', 'JavaScript', 'Java'],
                    interviewType: 'coding',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };

                await set(interviewRef, { ...newInterview, createdBy: userId });
                this.setState({
                    interview: newInterview
                }, () => {
                    this.updatePromptPreview();
                });
            }

            // Real-time synchronization for collaborative editing
            let isInitialLoad = true;
            onValue(interviewRef, (snapshot) => {
                if (snapshot.exists() && !isInitialLoad) {
                    const data = snapshot.val();

                    // Don't overwrite while user is typing in code editor (like Coding.js)
                    if (this.isTypingCode) return;
                    // Only update from server if we don't have pending local changes
                    if (!this.state.hasChanges) {
                        this.setState({
                            interview: {
                                ...this.state.interview,
                                ...data
                            }
                        }, () => {
                            this.updatePromptPreview();
                        });
                    }
                }
                isInitialLoad = false;
            });

        } catch (error) {
            console.error('Error loading interview:', error);
        }
    }

    handleInputChange(field: keyof InterviewSettings, value: any, shouldAutoSave: boolean = false): void {
        this.setState({
            interview: {
                ...this.state.interview,
                [field]: value
            },
            hasChanges: true
        }, () => {
            // Update prompt preview when relevant fields change
            if (['title', 'description', 'content', 'instructions', 'difficulty', 'timeLimit', 'allowedLanguages'].includes(field)) {
                this.updatePromptPreview();
            }
            if (shouldAutoSave) {
                this.saveInterview(true);
            }
        });
    }

    handleCodeChange(code: string): void {
        this.isTypingCode = true;
        if (this.codeFirebaseTimeout) {
            clearTimeout(this.codeFirebaseTimeout);
        }

        this.setState({
            interview: {
                ...this.state.interview,
                content: code
            },
            hasChanges: true
        }, () => {
            this.updatePromptPreview();
        });

        // Direct Firebase write on debounce (like Coding.js)
        this.codeFirebaseTimeout = setTimeout(() => this.writeCodeToFirebase(), 250);
    }

    private writeCodeToFirebase(): void {
        this.codeFirebaseTimeout = null;
        this.isTypingCode = false;
        const userId = this.state.user?.uid;
        if (!userId) return;
        const content = this.codeMirrorRef?.getCodeMirror?.()?.getValue?.() ?? this.state.interview.content ?? '';
        const interviewRef = ref(this.db, `company-interviews/${userId}/${this.props.interviewId}`);
        update(interviewRef, {
            content,
            updatedAt: new Date().toISOString()
        }).catch((err) => {
            console.error('Error saving code to Firebase:', err);
        });
    }

    toggleAgentConfig(): void {
        this.setState(prevState => ({
            showAgentConfig: !prevState.showAgentConfig
        }));
    }

    loadDefaultPrompt(): void {
        const defaultPrompt = getBasePromptTemplate();
        this.setState({
            interview: {
                ...this.state.interview,
                agentSystemPrompt: defaultPrompt
            },
            hasChanges: true
        }, () => {
            this.updatePromptPreview();
        });
    }

    updatePromptPreview(): void {
        const preview = getPromptPreview(this.state.interview, this.state.interview.agentSystemPrompt);
        this.setState({
            promptPreview: preview
        });
    }

    openPreviewModal(): void {
        this.setState({ showPreviewModal: true });
    }

    closePreviewModal(): void {
        this.setState({ showPreviewModal: false });
    }

    toggleCandidateProfile(): void {
        this.setState(prevState => ({
            showCandidateProfile: !prevState.showCandidateProfile
        }));
    }

    async handleDocumentUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const userId = this.state.user?.uid;
        if (!userId) return;

        const interviewId = this.props.interviewId;
        this.setState({ uploadingDocument: true });

        try {
            const newDocs: CandidateDocument[] = [...(this.state.interview.candidateDocuments || [])];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileRef = storageRef(this.storage, `interview-documents/${userId}/${interviewId}/${file.name}`);
                await uploadBytes(fileRef, file);
                const downloadURL = await getDownloadURL(fileRef);

                newDocs.push({
                    name: file.name,
                    url: downloadURL,
                    type: file.name.toLowerCase().includes('resume') || file.name.toLowerCase().includes('cv') ? 'resume' : 'other',
                    uploadedAt: new Date().toISOString(),
                    size: file.size
                });
            }

            this.setState({
                interview: {
                    ...this.state.interview,
                    candidateDocuments: newDocs
                },
                hasChanges: true,
                uploadingDocument: false
            }, () => {
                this.saveInterview(true);
            });
        } catch (error) {
            console.error('Error uploading document:', error);
            this.setState({ uploadingDocument: false });
            alert('Error uploading document. Please try again.');
        }

        // Reset file input
        if (this.fileInputRef.current) {
            this.fileInputRef.current.value = '';
        }
    }

    async removeDocument(index: number): Promise<void> {
        const docs = [...(this.state.interview.candidateDocuments || [])];
        const doc = docs[index];

        try {
            // Try to delete from storage
            const userId = this.state.user?.uid;
            if (userId && doc) {
                const fileRef = storageRef(this.storage, `interview-documents/${userId}/${this.props.interviewId}/${doc.name}`);
                try {
                    await deleteObject(fileRef);
                } catch (e) {
                    // File may not exist in storage, continue anyway
                    console.warn('Could not delete file from storage:', e);
                }
            }

            docs.splice(index, 1);
            this.setState({
                interview: {
                    ...this.state.interview,
                    candidateDocuments: docs
                },
                hasChanges: true
            }, () => {
                this.saveInterview(true);
            });
        } catch (error) {
            console.error('Error removing document:', error);
        }
    }

    async runCandidateResearch(): Promise<void> {
        const { interview } = this.state;

        if (!interview.candidateLinkedIn && (!interview.candidateDocuments || interview.candidateDocuments.length === 0)) {
            alert('Please provide a LinkedIn URL or upload a document before running research.');
            return;
        }

        this.setState({
            researchLoading: true,
            researchError: null,
            interview: {
                ...this.state.interview,
                candidateResearchStatus: 'loading'
            }
        });

        try {
            const backendUrl = process.env.NODE_ENV === 'production'
                ? ''
                : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000');
            const response = await fetch(`${backendUrl}/api/candidate/research`, {
                method: 'POST',
                mode: 'cors',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    candidateName: interview.candidateName || '',
                    linkedInUrl: interview.candidateLinkedIn || '',
                    resumeText: '', // Could be extracted from uploaded docs in the future
                    interviewTitle: interview.title || ''
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || errorData.details || `Research request failed (${response.status})`);
            }

            const data = await response.json();

            this.setState({
                interview: {
                    ...this.state.interview,
                    candidateResearchData: data.research,
                    candidateResearchStatus: 'done'
                },
                researchLoading: false,
                hasChanges: true
            }, () => {
                this.updatePromptPreview();
                this.saveInterview(true);
            });
        } catch (error: any) {
            console.error('Error running candidate research:', error);
            const errorMessage = error.message && error.message !== 'Failed to fetch'
                ? error.message
                : 'Could not connect to the research server. Make sure the backend is running on port 8000.';
            this.setState({
                interview: {
                    ...this.state.interview,
                    candidateResearchStatus: 'error'
                },
                researchLoading: false,
                researchError: errorMessage
            });
        }
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    // Helper function to remove undefined values from object (Firebase doesn't accept undefined)
    removeUndefinedValues(obj: any): any {
        if (Array.isArray(obj)) {
            return obj.map(item => {
                if (item !== null && typeof item === 'object') {
                    return this.removeUndefinedValues(item);
                }
                return item;
            }).filter(item => item !== undefined);
        }

        const cleaned: any = {};
        for (const key in obj) {
            if (obj[key] !== undefined) {
                if (obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
                    cleaned[key] = this.removeUndefinedValues(obj[key]);
                } else if (Array.isArray(obj[key])) {
                    cleaned[key] = this.removeUndefinedValues(obj[key]);
                } else {
                    cleaned[key] = obj[key];
                }
            }
        }
        return cleaned;
    }

    async saveInterview(silent: boolean = false): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const interviewId = this.props.interviewId;

        // Cancel pending debounced code save - we're doing a full save now
        if (this.codeFirebaseTimeout) {
            clearTimeout(this.codeFirebaseTimeout);
            this.codeFirebaseTimeout = null;
            this.isTypingCode = false;
        }

        try {
            // Get content directly from CodeMirror if available - ensures we save what's displayed
            // (avoids stale state when user pastes and immediately clicks Save)
            let contentToSave = this.state.interview.content;
            if (this.codeMirrorRef?.getCodeMirror?.()) {
                const cmContent = this.codeMirrorRef.getCodeMirror().getValue();
                if (cmContent !== undefined) {
                    contentToSave = cmContent;
                }
            }

            const interviewRef = ref(this.db, `company-interviews/${userId}/${interviewId}`);
            const updatedInterview = {
                ...this.state.interview,
                content: contentToSave,
                updatedAt: new Date().toISOString()
            };

            // Remove undefined values before saving (Firebase doesn't accept undefined)
            const cleanedInterview = this.removeUndefinedValues(updatedInterview);

            await update(interviewRef, cleanedInterview);
            this.setState({ hasChanges: false });
        } catch (error) {
            console.error('Error saving interview:', error);
            if (!silent) {
                alert('Error saving interview. Please try again.');
            }
        }
    }

    async publishInterview(): Promise<void> {
        const userId = this.state.user?.uid;
        if (!userId) return;

        const interviewId = this.props.interviewId;

        try {
            const interviewRef = ref(this.db, `company-interviews/${userId}/${interviewId}`);
            const updatedInterview: InterviewSettings = {
                ...this.state.interview,
                status: 'active',
                updatedAt: new Date().toISOString()
            };

            // Remove undefined values before saving (Firebase doesn't accept undefined)
            const cleanedInterview = this.removeUndefinedValues(updatedInterview);

            await update(interviewRef, cleanedInterview);

            // Create sandbox entry so the interview is accessible at /box/{interviewId}
            const languageMap: { [key: string]: { id: number; mode: string } } = {
                'JavaScript': { id: 63, mode: 'javascript' },
                'Python': { id: 71, mode: 'python' },
                'Ruby': { id: 72, mode: 'ruby' },
                'Swift': { id: 83, mode: 'swift' },
                'Java': { id: 62, mode: 'clike' },
                'Go': { id: 60, mode: 'go' },
                'PHP': { id: 68, mode: 'php' },
                'R': { id: 80, mode: 'r' }
            };

            const langInfo = languageMap[this.state.interview.language] || { id: 71, mode: 'python' };
            const createdon = new Date().toLocaleString();

            // Create public sandbox entry for the interview (includes full interview context for voice agent)
            const sandboxRef = ref(this.db, `code-sessions/${interviewId}`);
            await set(sandboxRef, {
                content: this.state.interview.content || '',
                createdon: createdon,
                language: this.state.interview.language,
                language_id: langInfo.id,
                mode: langInfo.mode,
                user: userId,
                isInterview: true,
                interviewId: interviewId,
                interviewType: this.state.interview.interviewType || 'coding',
                interviewTitle: this.state.interview.title,
                interviewDescription: this.state.interview.description,
                interviewInstructions: this.state.interview.instructions,
                interviewDifficulty: this.state.interview.difficulty,
                interviewTimeLimit: this.state.interview.timeLimit,
                interviewAllowedLanguages: this.state.interview.allowedLanguages || [],
                systemDesignProblem: this.state.interview.systemDesignProblem || null,
                systemDesignPrompt: this.state.interview.systemDesignPrompt || null,
                // Candidate profile for voice agent context
                candidateName: this.state.interview.candidateName || '',
                candidateLinkedIn: this.state.interview.candidateLinkedIn || '',
                candidateDocuments: this.state.interview.candidateDocuments || [],
                candidateResearchData: this.state.interview.candidateResearchData || '',
                agentSystemPrompt: this.state.interview.agentSystemPrompt || null
            });

            this.setState({
                interview: updatedInterview,
                hasChanges: false,
                publishSuccess: `/box/${interviewId}`
            });

            // Clear success message after 5 seconds
            setTimeout(() => {
                this.setState({ publishSuccess: null });
            }, 5000);
        } catch (error) {
            console.error('Error publishing interview:', error);
            alert('Error publishing interview. Please try again.');
        }
    }

    async handleLogout() {
        try {
            await this.auth.signOut();
            this.props.navigate('/login');
        } catch (error) {
            console.error('Logout failed', error);
        }
    }

    getLanguageMode(language: string): string {
        const modes: { [key: string]: string } = {
            'JavaScript': 'javascript',
            'Python': 'python',
            'Ruby': 'ruby',
            'Swift': 'swift',
            'Java': 'clike', // Using clike mode for Java
            'Go': 'go',
            'PHP': 'php',
            'R': 'r'
        };
        return modes[language] || 'python';
    }

    render(): React.ReactElement {
        const { darkMode } = this.props;
        const { login, loading, interview, hasChanges } = this.state;

        if (loading) {
            return (
                <div className={`dashboard-loading ${darkMode ? 'dashboard-loading-dark' : ''}`}>
                    <div className="loading-spinner">Loading...</div>
                </div>
            );
        }

        if (!login) {
            return <div></div>;
        }

        return (
            <div className={`company-interview-editor ${darkMode ? 'company-interview-editor-dark' : ''}`}>
                <DashboardSidebar
                    darkMode={darkMode}
                    accountType="company"
                    userName={this.state.user?.displayName || 'User'}
                    companyName="Streamatrix"
                    profilePhotoUrl={this.state.user?.photoURL}
                    onLogout={this.handleLogout}
                />
                <Header darkMode={darkMode} />

                <div className="editor-container editor-container-with-chat">
                    {/* Header Controls */}
                    <div className="editor-header editor-header-full-width">
                        <div className="editor-title">
                            <h1>Interview Editor</h1>
                            <span className="interview-id">ID: {this.props.interviewId}</span>
                        </div>

                        <div className="editor-actions">
                            <button
                                className={`save-btn ${hasChanges ? 'has-changes' : ''}`}
                                onClick={() => this.saveInterview(false)}
                            >
                                <FiSave />
                                Save
                            </button>

                            {interview.status === 'draft' && (
                                <button
                                    className="publish-btn"
                                    onClick={this.publishInterview}
                                >
                                    <FiEye />
                                    Publish
                                </button>
                            )}

                            {interview.status === 'active' && (
                                <button
                                    className="publish-btn republish-btn"
                                    onClick={this.publishInterview}
                                    title="Update the sandbox with latest interview data (candidate profile, problem, etc.)"
                                >
                                    <FiRefreshCw />
                                    Republish
                                </button>
                            )}

                            <button className="preview-btn" onClick={this.openPreviewModal}>
                                <FiUsers />
                                Preview
                            </button>

                            {interview.status === 'active' && (
                                <a
                                    href={`/box/${this.props.interviewId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="open-sandbox-btn"
                                    onClick={() => {
                                        sessionStorage.setItem('darkMode', String(this.props.darkMode));
                                    }}
                                >
                                    <FiExternalLink />
                                    Open Sandbox
                                </a>
                            )}
                        </div>
                    </div>

                    {this.state.publishSuccess && (
                        <div className="publish-success-banner">
                            <span>Interview published successfully! Candidates can now access it at <a href={this.state.publishSuccess || '#'} target="_blank" rel="noopener noreferrer">{this.state.publishSuccess}</a></span>
                            <button className="publish-success-dismiss" onClick={() => this.setState({ publishSuccess: null })} aria-label="Dismiss">×</button>
                        </div>
                    )}

                    <div className="editor-main-row">
                        <div className="editor-content-column">
                    {/* Interview Settings */}
                    <div className="interview-settings">
                        <div className="settings-grid">
                            <div className="setting-group">
                                <label>Interview Title</label>
                                <input
                                    type="text"
                                    value={interview.title}
                                    onChange={(e) => this.handleInputChange('title', e.target.value)}
                                    onBlur={() => this.saveInterview(true)}
                                    placeholder="Enter interview title"
                                />
                            </div>

                            <div className="setting-group">
                                <label>Interview Type</label>
                                <select
                                    value={interview.interviewType || 'coding'}
                                    onChange={(e) => this.handleInputChange('interviewType', e.target.value as 'coding' | 'system-design', true)}
                                >
                                    <option value="coding">Coding</option>
                                    <option value="system-design">System Design</option>
                                </select>
                                {(interview.interviewType || 'coding') === 'system-design' && (
                                    <span className="setting-hint">Candidates will use the Diagram tab for this interview.</span>
                                )}
                            </div>

                            <div className="setting-group">
                                <label>Difficulty</label>
                                <select
                                    value={interview.difficulty}
                                    onChange={(e) => this.handleInputChange('difficulty', e.target.value, true)}
                                >
                                    <option value="Easy">Easy</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Hard">Hard</option>
                                </select>
                            </div>

                            <div className="setting-group">
                                <label>Time Limit (minutes)</label>
                                <input
                                    type="number"
                                    value={interview.timeLimit}
                                    onChange={(e) => this.handleInputChange('timeLimit', parseInt(e.target.value))}
                                    onBlur={() => this.saveInterview(true)}
                                    min="15"
                                    max="180"
                                />
                            </div>

                            <div className="setting-group">
                                <label>Status</label>
                                <select
                                    value={interview.status}
                                    onChange={(e) => this.handleInputChange('status', e.target.value, true)}
                                >
                                    <option value="draft">Draft</option>
                                    <option value="active">Active</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>
                        </div>

                        <div className="setting-group full-width">
                            <label>Description</label>
                            <textarea
                                value={interview.description}
                                onChange={(e) => this.handleInputChange('description', e.target.value)}
                                onBlur={() => this.saveInterview(true)}
                                placeholder="Describe what this interview is about..."
                                rows={3}
                            />
                        </div>

                        <div className="setting-group full-width">
                            <label>Instructions for Candidates</label>
                            <textarea
                                value={interview.instructions}
                                onChange={(e) => this.handleInputChange('instructions', e.target.value)}
                                onBlur={() => this.saveInterview(true)}
                                placeholder="Provide clear instructions for candidates..."
                                rows={4}
                            />
                        </div>

                        {/* Candidate Profile Section */}
                        <div className="setting-group full-width candidate-profile-section">
                            <div className="candidate-profile-header" onClick={this.toggleCandidateProfile}>
                                <label>
                                    <FiUser style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                    Candidate Profile
                                </label>
                                {this.state.showCandidateProfile ? <FiChevronUp /> : <FiChevronDown />}
                            </div>

                            {this.state.showCandidateProfile && (
                                <div className="candidate-profile-content">
                                    <div className="candidate-profile-description">
                                        <p>Add candidate information to personalize the interview experience. Upload their resume, add their LinkedIn profile, and run background research to give the AI interviewer relevant context.</p>
                                    </div>

                                    <div className="candidate-info-grid">
                                        <div className="setting-group">
                                            <label><FiUser style={{ marginRight: '6px', verticalAlign: 'middle' }} />Candidate Name</label>
                                            <input
                                                type="text"
                                                value={interview.candidateName || ''}
                                                onChange={(e) => this.handleInputChange('candidateName', e.target.value)}
                                                onBlur={() => this.saveInterview(true)}
                                                placeholder="Enter candidate's full name"
                                            />
                                        </div>

                                        <div className="setting-group">
                                            <label><FiLink style={{ marginRight: '6px', verticalAlign: 'middle' }} />LinkedIn Profile</label>
                                            <div className="linkedin-input-wrapper">
                                                <input
                                                    type="url"
                                                    value={interview.candidateLinkedIn || ''}
                                                    onChange={(e) => this.handleInputChange('candidateLinkedIn', e.target.value)}
                                                    onBlur={() => this.saveInterview(true)}
                                                    placeholder="https://linkedin.com/in/candidate"
                                                />
                                                {interview.candidateLinkedIn && (
                                                    <a
                                                        href={interview.candidateLinkedIn}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="linkedin-open-btn"
                                                        title="Open LinkedIn profile"
                                                    >
                                                        <FiExternalLink />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Document Upload */}
                                    <div className="document-upload-section">
                                        <label><FiUpload style={{ marginRight: '6px', verticalAlign: 'middle' }} />Documents</label>
                                        <div
                                            className="document-upload-zone"
                                            onClick={() => this.fileInputRef.current?.click()}
                                        >
                                            <FiUpload className="upload-icon" />
                                            <p>{this.state.uploadingDocument ? 'Uploading...' : 'Click to upload or drag & drop'}</p>
                                            <span>PDF, DOC, DOCX, TXT (Max 10MB)</span>
                                            <input
                                                ref={this.fileInputRef}
                                                type="file"
                                                accept=".pdf,.doc,.docx,.txt"
                                                multiple
                                                onChange={this.handleDocumentUpload}
                                                style={{ display: 'none' }}
                                            />
                                        </div>

                                        {/* Uploaded Documents List */}
                                        {interview.candidateDocuments && interview.candidateDocuments.length > 0 && (
                                            <div className="document-list">
                                                {interview.candidateDocuments.map((doc, index) => (
                                                    <div key={index} className="document-item">
                                                        <div className="document-info">
                                                            <span className="document-name">{doc.name}</span>
                                                            <span className="document-meta">
                                                                {this.formatFileSize(doc.size)} • {doc.type}
                                                            </span>
                                                        </div>
                                                        <div className="document-actions">
                                                            <a
                                                                href={doc.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="document-view-btn"
                                                                title="View document"
                                                            >
                                                                <FiExternalLink />
                                                            </a>
                                                            <button
                                                                className="document-remove-btn"
                                                                onClick={() => this.removeDocument(index)}
                                                                title="Remove document"
                                                            >
                                                                <FiTrash2 />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Research Section */}
                                    <div className="research-section">
                                        <div className="research-header">
                                            <label><FiSearch style={{ marginRight: '6px', verticalAlign: 'middle' }} />Background Research</label>
                                            <button
                                                className={`research-btn ${this.state.researchLoading ? 'research-loading' : ''}`}
                                                onClick={this.runCandidateResearch}
                                                disabled={this.state.researchLoading}
                                            >
                                                {this.state.researchLoading ? (
                                                    <><span className="research-spinner"></span>Researching...</>
                                                ) : (
                                                    <><FiSearch style={{ marginRight: '4px' }} />Research Candidate</>
                                                )}
                                            </button>
                                        </div>

                                        {this.state.researchError && (
                                            <div className="research-error">
                                                {this.state.researchError}
                                            </div>
                                        )}

                                        {interview.candidateResearchData && (
                                            <div className="research-results">
                                                <div className="research-results-header">
                                                    <span className="research-status-badge">✓ Research Complete</span>
                                                </div>
                                                <div className="research-results-content markdown-content">
                                                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                                        {interview.candidateResearchData}
                                                    </ReactMarkdown>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Agent Configuration Section */}
                        <div className="setting-group full-width agent-config-section">
                            <div className="agent-config-header" onClick={this.toggleAgentConfig}>
                                <label>
                                    <FiSettings style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                    Agent System Prompt Configuration
                                </label>
                                {this.state.showAgentConfig ? <FiChevronUp /> : <FiChevronDown />}
                            </div>

                            {this.state.showAgentConfig && (
                                <div className="agent-config-content">
                                    <div className="agent-config-description">
                                        <p>Customize the system prompt for the LiveKit interviewer agent. This prompt defines how the agent will conduct the interview, evaluate candidates, and interact with them.</p>
                                        <p>If left empty, the default template will be used with your interview settings automatically filled in.</p>
                                    </div>

                                    <div className="agent-config-actions">
                                        <button
                                            type="button"
                                            className="default-prompt-btn"
                                            onClick={this.loadDefaultPrompt}
                                        >
                                            <FiRefreshCw style={{ marginRight: '4px' }} />
                                            Use Default Template
                                        </button>
                                    </div>

                                    <div className="agent-prompt-editor">
                                        <label>Custom Agent System Prompt</label>
                                        <textarea
                                            value={interview.agentSystemPrompt || ''}
                                            onChange={(e) => {
                                                const validation = validateCustomPrompt(e.target.value);
                                                this.handleInputChange('agentSystemPrompt', e.target.value);
                                            }}
                                            onBlur={() => this.saveInterview(true)}
                                            placeholder="Enter custom system prompt, or leave empty to use default template..."
                                            rows={12}
                                            className="agent-prompt-textarea"
                                        />
                                        <div className="agent-prompt-hint">
                                            <small>
                                                Tip: Use variables like {'{interviewTitle}'}, {'{difficulty}'}, {'{timeLimit}'}, {'{problemContent}'} to reference interview settings.
                                            </small>
                                        </div>
                                    </div>

                                    <div className="agent-prompt-preview">
                                        <label>Prompt Preview (with interview settings applied)</label>
                                        <div className="prompt-preview-content">
                                            {this.state.promptPreview || 'Loading preview...'}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Code Editor */}
                    <div className="code-editor-section">
                        <div className="editor-header">
                            <h3>Coding Problem</h3>
                            <div className="language-selector">
                                <label>Language:</label>
                                <Dropdown
                                    title="Select Language"
                                    list={[
                                        { id: 1, language_id: 1, title: 'JavaScript', mode: 'javascript', fa_icon: 'FaJsSquare', selected: false, key: 'javascript' },
                                        { id: 2, language_id: 2, title: 'Python', mode: 'python', fa_icon: 'FaPython', selected: false, key: 'python' },
                                        { id: 3, language_id: 3, title: 'Ruby', mode: 'ruby', fa_icon: 'FaGem', selected: false, key: 'ruby' },
                                        { id: 4, language_id: 4, title: 'Swift', mode: 'swift', fa_icon: 'FaSwift', selected: false, key: 'swift' },
                                        { id: 5, language_id: 5, title: 'Java', mode: 'clike', fa_icon: 'FaJava', selected: false, key: 'java' },
                                        { id: 6, language_id: 6, title: 'Go', mode: 'go', fa_icon: 'FaGoogle', selected: false, key: 'go' },
                                        { id: 7, language_id: 7, title: 'PHP', mode: 'php', fa_icon: 'FaPhp', selected: false, key: 'php' },
                                        { id: 8, language_id: 8, title: 'R', mode: 'r', fa_icon: 'FaRProject', selected: false, key: 'r' }
                                    ]}
                                    toggleItem={(id: number, key: string) => {
                                        const languageMap: { [key: string]: string } = {
                                            'javascript': 'JavaScript',
                                            'python': 'Python',
                                            'ruby': 'Ruby',
                                            'swift': 'Swift',
                                            'java': 'Java',
                                            'go': 'Go',
                                            'php': 'PHP',
                                            'r': 'R'
                                        };
                                        this.handleInputChange('language', languageMap[key] || 'Python', true);
                                    }}
                                    currLang={interview.language}
                                />
                            </div>
                        </div>

                        <div className="code-editor">
                            <CodeMirror
                                ref={(ref: any) => { this.codeMirrorRef = ref; }}
                                value={interview.content ?? ''}
                                onChange={this.handleCodeChange}
                                options={{
                                    mode: this.getLanguageMode(interview.language),
                                    theme: 'dracula',
                                    lineNumbers: true,
                                    lineWrapping: true,
                                    autoCloseBrackets: true,
                                    matchBrackets: true,
                                    indentUnit: 4,
                                    tabSize: 4,
                                    readOnly: false
                                }}
                            />
                        </div>
                    </div>
                        </div>

                        <div className="editor-chat-column">
                            <InterviewEditorChatPanel
                                darkMode={darkMode}
                                userId={this.state.user?.uid ?? null}
                                interview={interview}
                                onSettingsUpdated={this.handleSettingsUpdated}
                            />
                        </div>
                    </div>
                </div>

                {/* Preview Modal */}
                {this.state.showPreviewModal && (
                    <div className="preview-modal-overlay" onClick={this.closePreviewModal}>
                        <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="preview-modal-header">
                                <h2>Interview Preview</h2>
                                <button className="preview-modal-close" onClick={this.closePreviewModal}>
                                    <FiX />
                                </button>
                            </div>
                            <div className="preview-modal-body">
                                <div className="preview-section">
                                    <h3>{interview.title || 'Untitled Interview'}</h3>
                                    <p className="preview-meta">
                                        <span>Difficulty: {interview.difficulty || 'Medium'}</span>
                                        <span>Time Limit: {interview.timeLimit || 60} minutes</span>
                                        <span>Language: {interview.language || 'Python'}</span>
                                    </p>
                                </div>

                                {interview.description && (
                                    <div className="preview-section">
                                        <h4>Description</h4>
                                        <p>{interview.description}</p>
                                    </div>
                                )}

                                {interview.instructions && (
                                    <div className="preview-section">
                                        <h4>Instructions</h4>
                                        <p>{interview.instructions}</p>
                                    </div>
                                )}

                                {interview.content && (
                                    <div className="preview-section">
                                        <h4>Coding Problem</h4>
                                        <div className="preview-code-block">
                                            <pre><code>{interview.content}</code></pre>
                                        </div>
                                    </div>
                                )}

                                <div className="preview-section">
                                    <h4>What Candidates Will See</h4>
                                    <p className="preview-note">
                                        This is how the interview will appear to candidates. They will be able to:
                                    </p>
                                    <ul className="preview-features">
                                        <li>View the problem statement and instructions</li>
                                        <li>Write and run code in the selected language</li>
                                        <li>Interact with the AI interviewer agent</li>
                                        <li>Use the whiteboard for diagrams and explanations</li>
                                        <li>See their progress and remaining time</li>
                                    </ul>
                                </div>
                            </div>

                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default CompanyInterviewEditorWrapper;
